import { useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useRouter } from "next/router";

import { logout } from "../../core/retailShop.core";
import { useTranslation } from "../../core/translation.core";
import useAdminShop from "../../hooks/useAdminShop";
import CashIcon from "../../icons/posicons/cash.icon";
import CustomersIcon from "../../icons/posicons/customers.icon";
import DocumentDuplicateIcon from "../../icons/posicons/documentDuplicate.icon";
import TruckIcon from "../../icons/truck.icon";
import LogoutIcon from "../../icons/posicons/logout.icon";
import NewOrderIcon from "../../icons/posicons/neworder.icon";
import OrderListIcon from "../../icons/posicons/orderList.icon";
import ProductsIcon from "../../icons/posicons/products.icon";
import SettingIcon from "../../icons/posicons/setting.icon";

const iconStyle = "h-10 w-10";

const linkStyles =
  " cursor-pointer font-semibold text-base flex flex-col justify-center items-center text-sm p-6 sm:p-8 md:p-10 border-2  rounded-[20px] rounded-md ";
const linkActive = " border-[#3878F7] text-[#3878F7] bg-[#F5F9FE] ";

export default function MobilePosNav({ className, toggleShowNav }: { className?: string; toggleShowNav: VoidFunction }) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const shop = useAdminShop();

  return (
    <section
      className={`${className} text-her-blue bg-white dark:text-white dark:bg-her-darkblue2 grid grid-cols-2 md:grid-cols-3 items-center gap-4 px-6`}
    >
      <Item id="new-order" href="/" onClick={toggleShowNav}>
        <NewOrderIcon className={iconStyle} />
        <span>{t(msg.posMenuItems.newOrder)}</span>
      </Item>

      <Item id="order-list" href="/orders" onClick={toggleShowNav}>
        <OrderListIcon className={iconStyle} />
        <span>{t(msg.posMenuItems.history)}</span>
      </Item>

      <Item id="product" href="/products" onClick={toggleShowNav}>
        <ProductsIcon className={iconStyle} />
        <span>{t(msg.posMenuItems.products)}</span>
      </Item>

      <Item id="warehouse-transfer" href="/warehouseTransfer" onClick={toggleShowNav}>
        <DocumentDuplicateIcon className={iconStyle} />
        <span>{t(msg.posMenuItems.warehouseTransfer)}</span>
      </Item>

      <Item id="settlement" href="/settlement" onClick={toggleShowNav}>
        <CashIcon className={iconStyle} />
        <span>{t(msg.posMenuItems.settlement)}</span>
      </Item>

      <Item id="customer" href="/customers" onClick={toggleShowNav}>
        <CustomersIcon className={iconStyle} />
        <span>{t(msg.posMenuItems.customers)}</span>
      </Item>

      {shop?.posDeliveryMenuEnabled && (
        <Item id="delivery-schedules" href="/deliveryPickup" onClick={toggleShowNav}>
          <TruckIcon className={iconStyle} />
          <span>{t(msg.posMenuItems.deliveryPickup)}</span>
        </Item>
      )}

      <Item id="setting" href="/setting" onClick={toggleShowNav}>
        <SettingIcon className={iconStyle} />
        <span>{t(msg.posMenuItems.setting)}</span>
      </Item>

      <div className={linkStyles}>
        <button className="flex-center flex-col" onClick={() => logout(queryClient)}>
          <LogoutIcon className={iconStyle} />
          <span className="pt-2">{t(msg.menu.logout)}</span>
        </button>
      </div>
    </section>
  );
}

const Item = ({
  id,
  href,
  children,
  onClick,
}: {
  id?: string;
  href?: string;
  children: React.ReactNode;
  onClick: VoidFunction;
}) => {
  const { pathname } = useRouter();
  const isActive =
    href === "/"
      ? pathname === "/"
      : Boolean(href && pathname.startsWith(href));

  return (
    <div onClick={onClick}>
      {/* @ts-ignore */}
      <Link id={id} href={href} className={linkStyles + (isActive ? linkActive : " ")}>
        {children}
      </Link>
    </div>
  );
};
